import {
    FieldError,
    UseFormClearErrors,
    UseFormRegister,
    UseFormSetError,
    UseFormSetValue,
} from 'react-hook-form';
import { Box, FormError, FormField, FormInputGroup, FormLabel, Typography } from '@vp/swan';
import { FormInput } from '../BriefForm';
import { useIsEditMode, useIsReviewMode } from '../ViewContext';
import { FieldFragment } from '../brief.generated';
import FieldDisplay from './FieldDisplay';
import FieldInput from './FieldInput';
import { useFieldTitleWithOverrides } from './FieldReview';
import RequiredIndicator from './RequiredIndicator';
import { isDisplayReview, useIsFieldHidden } from './useIsFieldHidden';

type FieldProps = {
    field: FieldFragment;
    error: FieldError | undefined;
    register: UseFormRegister<FormInput>;
    setValue: UseFormSetValue<FormInput>;
    clearErrors: UseFormClearErrors<FormInput>;
    setError: UseFormSetError<FormInput>;
    isMultiFieldGroup?: boolean;
    isIntro?: boolean;
    isOmitRequiredIndicator?: boolean;
    isOverrideDisplayConfig?: boolean;
};

export default function Field({
    field,
    error,
    register,
    setValue,
    clearErrors,
    setError,
    isMultiFieldGroup,
    isIntro,
    isOmitRequiredIndicator,
    isOverrideDisplayConfig,
}: FieldProps) {
    const isEditMode = useIsEditMode();
    const isHidden = useIsFieldHidden(field, isEditMode, isOverrideDisplayConfig);
    const isReviewMode = useIsReviewMode();

    const fieldLabel = useFieldTitleWithOverrides(field);
    const descriptiveText = getFieldDescriptiveText(field);

    if (isHidden) {
        return null;
    }

    const fieldComponent = isDisplayReview(field.display, isReviewMode || false, isEditMode) ? (
        <FieldDisplay field={field} />
    ) : (
        <FieldInput
            field={field}
            register={register}
            setValue={setValue}
            error={error}
            clearErrors={clearErrors}
            setError={setError}
            aria-describedby={`${field.id}-error`}
        />
    );

    return (
        <FormField
            mt={isMultiFieldGroup && !isIntro ? 6 : 0}
            data-testid={`brief-field-${field.id}`}
        >
            {fieldLabel !== '' && (
                <FormLabel
                    id={field.id}
                    htmlFor={field.id}
                    data-testid={`brief-field-label-${field.id}`}
                    fontSkin={
                        field.__typename === 'MultiGridChoiceField'
                            ? 'body-standard'
                            : 'body-standard-bold'
                    }
                    marginBottom={'3'}
                >
                    <span
                        dangerouslySetInnerHTML={{
                            __html: fieldLabel,
                        }}
                    />
                    <RequiredIndicator
                        isRequired={!!field.required}
                        isOmitted={
                            field.__typename === 'WorkEntityField' || isOmitRequiredIndicator
                        }
                    />

                    {field.__typename === 'MultiGridChoiceField' && field.validationPrompt && (
                        <Typography fontSize="xsmall" as="span" ml={'2'}>
                            {`(${field.validationPrompt})`}
                        </Typography>
                    )}
                </FormLabel>
            )}
            {descriptiveText && (
                <Typography
                    marginTop={'2'}
                    marginBottom={'4'}
                    data-testid={`brief-field-description-${field.id}`}
                >
                    {descriptiveText}
                </Typography>
            )}
            <FormInputGroup
                mt={isMultiFieldGroup || isIntro ? 0 : 6}
                data-testid={`brief-field-input-group-${field.id}`}
            >
                {error && (
                    <Box mb={'4'} data-testid={`brief-field-error-container-${field.id}`}>
                        <FormError
                            role="alert"
                            id={`${field.id}-error`}
                            marginTop={'4'}
                            data-testid={`brief-field-error-${field.id}`}
                        >
                            {error.message}
                        </FormError>
                    </Box>
                )}
                {fieldComponent}
            </FormInputGroup>
        </FormField>
    );
}

function getFieldDescriptiveText(field: FieldFragment): string {
    if (field.__typename === 'FilesField' || field.__typename === 'InputField') {
        return field.descriptiveText || '';
    }

    return '';
}
